import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { supabase } from '../../lib/supabase';
import { getSubscriptionPlans, createSubscription, createFreeSubscription, SubscriptionPlan } from '../../services/supabaseService';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet';
import Icon from '../../components/AppIcon';
import Logo from '../../components/Logo';
import Input from '../../components/ui/Input';
import Button from '../../components/ui/Button';
import { Checkbox } from '../../components/ui/Checkbox';
import PasswordStrengthIndicator from './components/PasswordStrengthIndicator';
import SocialRegistration from './components/SocialRegistration';
import RoleSelector from './components/RoleSelector';
import SecurityFeatures from './components/SecurityFeatures';
import { RegisterFormData, FormErrors, PasswordStrength, SocialProvider } from './types';
import MobileAppDownloads from '../../components/MobileAppDownloads';

const Register = () => {
  const navigate = useNavigate();
  const [isLoading, setIsLoading] = useState(false);
  const [isLoadingPlans, setIsLoadingPlans] = useState(true);
  const [errors, setErrors] = useState<FormErrors>({});
  const [plans, setPlans] = useState<SubscriptionPlan[]>([]);
  const [selectedPlan, setSelectedPlan] = useState<string>('');
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);

  const [formData, setFormData] = useState<RegisterFormData>({
    fullName: '',
    email: '',
    phone: '',
    nationalId: '',
    password: '',
    confirmPassword: '',
    role: 'member',
    agreeToTerms: false
  });

  const [touched, setTouched] = useState<Record<string, boolean>>({});
  const [idPhotoFile, setIdPhotoFile] = useState<File | null>(null);
  const [idPhotoPreviewUrl, setIdPhotoPreviewUrl] = useState<string | null>(null);

  const socialProviders: SocialProvider[] = [
  {
    id: 'google',
    name: 'Google',
    icon: 'Mail',
    color: '#DB4437'
  },
  {
    id: 'facebook',
    name: 'Facebook',
    icon: 'Facebook',
    color: '#1877F2'
  },
  {
    id: 'apple',
    name: 'Apple',
    icon: 'Apple',
    color: '#000000'
  }];


  useEffect(() => {
    loadPlans();
  }, []);

  useEffect(() => {
    return () => {
      if (idPhotoPreviewUrl) {
        URL.revokeObjectURL(idPhotoPreviewUrl);
      }
    };
  }, [idPhotoPreviewUrl]);

  const loadPlans = async () => {
    try {
      setIsLoadingPlans(true);
      const plansData = await getSubscriptionPlans();
      setPlans(plansData);
      if (plansData.length > 0) {
        // Select the first plan (usually free plan) by default
        setSelectedPlan(plansData[0].id);
      } else {
        setErrors({ general: 'No subscription plans available. Please contact support.' });
      }
    } catch (error: any) {
      console.error('Failed to load plans:', error);
      setErrors({ general: 'Failed to load subscription plans. Please refresh the page.' });
    } finally {
      setIsLoadingPlans(false);
    }
  };

  const calculatePasswordStrength = (password: string): PasswordStrength => {
    const requirements = {
      minLength: password.length >= 8,
      hasUpperCase: /[A-Z]/.test(password),
      hasLowerCase: /[a-z]/.test(password),
      hasNumber: /\d/.test(password),
      hasSpecial: /[!@#$%^&*(),.?":{}|<>]/.test(password)
    };

    const score = Object.values(requirements).filter(Boolean).length;

    let label = 'Weak';
    let color = 'var(--color-destructive)';

    if (score >= 5) {
      label = 'Strong';
      color = 'var(--color-success)';
    } else if (score >= 3) {
      label = 'Medium';
      color = 'var(--color-warning)';
    }

    return { score, label, color, requirements };
  };

  const validateField = (name: keyof RegisterFormData, value: string | boolean): string => {
    switch (name) {
      case 'fullName':
        if (!value) return 'Full name is required';
        if (typeof value === 'string' && value.length < 2)
        return 'Name must be at least 2 characters';
        return '';

      case 'email':
        // Email is required only when creating a group (admin role)
        if (formData.role === 'admin') {
          if (!value) return 'Email is required';
          if (typeof value === 'string' && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value))
            return 'Please enter a valid email address';
        }
        return '';

      case 'phone':
        // Phone is required only when joining a group (member role)
        if (formData.role === 'member') {
          if (!value) return 'Phone number is required';
          if (typeof value === 'string') {
            const cleanPhone = value.replace(/[\s\-\(\)]/g, '');
            if (cleanPhone.startsWith('+')) {
              const phoneRegex = /^\+\d{10,15}$/;
              if (!phoneRegex.test(cleanPhone)) {
                return 'Enter valid phone with country code (e.g., +6785555555)';
              }
            } else {
              const phoneRegex = /^\d{7,10}$/;
              if (!phoneRegex.test(cleanPhone)) {
                return 'Enter valid phone number (7-10 digits)';
              }
            }
          }
        }
        return '';

      case 'nationalId':
        // National ID is required only when joining as member
        if (formData.role === 'member') {
          if (!value) return 'National ID is required';
          if (typeof value === 'string' && value.trim().length < 4) {
            return 'National ID looks too short';
          }
        }
        return '';

      case 'password':
        if (!value) return 'Password is required';
        if (typeof value === 'string') {
          const strength = calculatePasswordStrength(value);
          if (!strength.requirements.minLength) return 'Password must be at least 8 characters';
          if (strength.score < 3) return 'Password is too weak';
        }
        return '';

      case 'confirmPassword':
        if (!value) return 'Please confirm your password';
        if (value !== formData.password) return 'Passwords do not match';
        return '';

      case 'agreeToTerms':
        if (!value) return 'You must agree to the terms and conditions';
        return '';

      default:
        return '';
    }
  };

  const handleInputChange = (field: keyof RegisterFormData, value: string | boolean) => {
    setFormData((prev) => ({ ...prev, [field]: value }));

    if (touched[field]) {
      const error = validateField(field, value);
      setErrors((prev) => ({ ...prev, [field]: error }));
    }

    if (field === 'password' && touched.confirmPassword && formData.confirmPassword) {
      const confirmError = validateField('confirmPassword', formData.confirmPassword);
      setErrors((prev) => ({ ...prev, confirmPassword: confirmError }));
    }
  };

  const handleBlur = (field: keyof RegisterFormData) => {
    setTouched((prev) => ({ ...prev, [field]: true }));
    const value = formData[field];
    const error = validateField(field, value);
    setErrors((prev) => ({ ...prev, [field]: error }));
  };

  const validateForm = (): boolean => {
    const newErrors: FormErrors = {};
    let isValid = true;

    // Validate all fields
    (Object.keys(formData) as Array<keyof RegisterFormData>).forEach((key) => {
      // Skip email validation if role is member, skip phone validation if role is admin
      if (key === 'email' && formData.role === 'member') return;
      if (key === 'phone' && formData.role === 'admin') return;
      if (key === 'nationalId' && formData.role === 'admin') return;
      
      const error = validateField(key, formData[key]);
      if (error) {
        newErrors[key] = error;
        isValid = false;
      }
    });

    // Validate ID photo for members
    if (formData.role === 'member') {
      if (!idPhotoFile) {
        newErrors.idPhoto = 'National ID photo is required';
        isValid = false;
      } else {
        const allowedTypes = ['image/jpeg', 'image/png', 'image/jpg', 'image/webp'];
        if (!allowedTypes.includes(idPhotoFile.type)) {
          newErrors.idPhoto = 'Please upload a JPG, PNG, or WebP image';
          isValid = false;
        } else if (idPhotoFile.size > 5 * 1024 * 1024) {
          newErrors.idPhoto = 'Image must be 5MB or smaller';
          isValid = false;
        }
      }
    }

    // Validate plan selection
    if (formData.role === 'admin' && !selectedPlan) {
      newErrors.general = 'Please select a subscription plan';
      isValid = false;
    }

    setErrors(newErrors);
    setTouched({
      fullName: true,
      email: formData.role === 'admin',
      phone: formData.role === 'member',
      nationalId: formData.role === 'member',
      password: true,
      confirmPassword: true,
      agreeToTerms: true
    });

    return isValid;
  };

  const handleIdPhotoChange = (file: File | null) => {
    // Clear previous preview URL
    if (idPhotoPreviewUrl) {
      URL.revokeObjectURL(idPhotoPreviewUrl);
    }

    setIdPhotoFile(file);
    setTouched((prev) => ({ ...prev, idPhoto: true }));

    if (!file) {
      setIdPhotoPreviewUrl(null);
      setErrors((prev) => ({ ...prev, idPhoto: 'National ID photo is required' }));
      return;
    }

    const allowedTypes = ['image/jpeg', 'image/png', 'image/jpg', 'image/webp'];
    if (!allowedTypes.includes(file.type)) {
      setIdPhotoPreviewUrl(null);
      setErrors((prev) => ({ ...prev, idPhoto: 'Please upload a JPG, PNG, or WebP image' }));
      return;
    }

    if (file.size > 5 * 1024 * 1024) {
      setIdPhotoPreviewUrl(null);
      setErrors((prev) => ({ ...prev, idPhoto: 'Image must be 5MB or smaller' }));
      return;
    }

    setErrors((prev) => ({ ...prev, idPhoto: undefined }));
    setIdPhotoPreviewUrl(URL.createObjectURL(file));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    setIsLoading(true);
    setErrors({});

    try {
      let authData;
      let signUpError;

      if (formData.role === 'admin') {
        // Creating group - use email
        const result = await supabase.auth.signUp({
          email: formData.email,
          password: formData.password,
          options: {
            data: {
              full_name: formData.fullName,
              role: 'group_admin',
            }
          }
        });
        authData = result.data;
        signUpError = result.error;
      } else {
        // Joining group - use phone as username (similar to mobile)
        let formattedPhone = formData.phone.trim();
        if (formattedPhone && !formattedPhone.startsWith('+')) {
          formattedPhone = '+678' + formattedPhone;
        }
        
        // Convert phone to auth email format (like mobile app)
        const cleanedPhone = formattedPhone.replace(/[^\d]/g, '');
        const authEmail = `phone_${cleanedPhone}@phone.local`;
        
        const result = await supabase.auth.signUp({
          email: authEmail,
          password: formData.password,
          options: {
            data: {
              full_name: formData.fullName,
              role: 'member',
              phone: formattedPhone,
              national_id: formData.nationalId,
            }
          }
        });
        authData = result.data;
        signUpError = result.error;
      }

      if (signUpError) throw signUpError;

      if (authData.user) {
        const userId = authData.user.id;
        
        // If admin, create subscription and redirect to payment/subscription page
        if (formData.role === 'admin') {
          try {
            // Get plan details to check if it's free or paid
            const { data: planData, error: planError } = await supabase
              .from('subscription_plans')
              .select('plan_type')
              .eq('id', selectedPlan)
              .single();

            if (planError) throw planError;

            const planType = planData?.plan_type || 'free';

            if (planType === 'free') {
              // For free plans, create subscription and redirect to dashboard
              await createFreeSubscription(userId);
              console.log('✅ Free subscription created');
              
              // Redirect to login - user can create group after login
              setTimeout(() => {
                navigate('/login', { 
                  state: { 
                    message: 'Registration successful! Please log in to create your group.' 
                  } 
                });
              }, 2000);
              return;
            } else {
              // For paid plans, create subscription and redirect to payment instructions
              await createSubscription(userId, selectedPlan);
              console.log('✅ Paid subscription created, redirecting to payment');
              
              // Redirect to payment instructions page
              setTimeout(() => {
                navigate('/payment-instructions', { 
                  state: { 
                    planId: selectedPlan,
                    fromRegistration: true
                  } 
                });
              }, 2000);
              return;
            }
          } catch (subscriptionError: any) {
            console.error('Error creating subscription during registration:', subscriptionError);
            setErrors({
              general: `Registration successful, but subscription creation failed: ${subscriptionError.message}. Please log in and complete your subscription setup.`
            });
            // Still navigate to login - user can complete subscription later
            setTimeout(() => {
              navigate('/login');
            }, 5000);
            return;
          }
        }

        // Create notification for super_admin about new user signup
        try {
          const { data: superAdmins, error: superAdminError } = await supabase
            .from('user_profiles')
            .select('id, full_name')
            .eq('role', 'super_admin');

          if (!superAdminError && superAdmins && superAdmins.length > 0) {
            const notificationPromises = superAdmins.map(async (admin) => {
              const { error } = await supabase
                .from('notifications')
                .insert({
                  user_id: admin.id,
                  type: 'member_joined',
                  title: 'New User Registration',
                  message: `New ${formData.role === 'admin' ? 'group admin' : 'member'} registered: ${formData.fullName}${formData.email ? ` (${formData.email})` : formData.phone ? ` (${formData.phone})` : ''}`,
                  related_entity_id: userId,
                  is_read: false,
                });

              if (error) {
                console.error(`Error creating notification for super admin ${admin.id}:`, error);
              }
            });

            await Promise.all(notificationPromises);
          }
        } catch (notifError) {
          console.error('Error creating super admin notifications for signup:', notifError);
        }

        // Show success message (admin case already handled above with redirect)
        if (formData.role === 'member') {
          // Upload National ID photo and store verification fields on profile
          try {
            if (!idPhotoFile) {
              throw new Error('National ID photo is required');
            }

            // Ensure we have an authenticated session (depends on Supabase email/phone confirmation settings)
            const { data: { session } } = await supabase.auth.getSession();
            if (!session) {
              throw new Error('Please log in to complete ID verification upload.');
            }

            const safeExt = idPhotoFile.name.split('.').pop() || 'jpg';
            const filePath = `${userId}/${Date.now()}-national-id.${safeExt}`;

            const { error: uploadError } = await supabase.storage
              .from('id-verifications')
              .upload(filePath, idPhotoFile, {
                contentType: idPhotoFile.type,
                upsert: true,
              });

            if (uploadError) throw uploadError;

            const { data: { publicUrl } } = supabase.storage
              .from('id-verifications')
              .getPublicUrl(filePath);

            const { error: profileUpdateError } = await supabase
              .from('user_profiles')
              .update({
                national_id: formData.nationalId,
                national_id_photo_url: publicUrl,
              })
              .eq('id', userId);

            if (profileUpdateError) throw profileUpdateError;
          } catch (uploadErr: any) {
            console.error('ID verification upload failed:', uploadErr);
            setErrors({
              general:
                `Registration successful, but ID verification upload failed: ${uploadErr?.message || 'Unknown error'}. ` +
                `Please log in and upload your National ID from your profile.`,
            });
            setTimeout(() => navigate('/login'), 5000);
            return;
          }

          setErrors({
            general:
              'Registration successful! Your National ID was submitted. Contact your group admin with your phone number to be added to a group.'
          });
        }

        // Navigate to login after 3 seconds
        setTimeout(() => {
          navigate('/login');
        }, 3000);
      }
    } catch (error: any) {
      console.error('Registration error:', error);
      setErrors({
        general: error.message || 'Registration failed. Please try again.'
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleSocialRegister = (providerId: string) => {
    console.log(`Registering with ${providerId}`);
  };

  const passwordStrength = calculatePasswordStrength(formData.password);

  return (
    <>
      <Helmet>
        <title>Register - communitySaving</title>
        <meta name="description" content="Create your communitySaving account to start managing community contributions" />
      </Helmet>

      <div className="min-h-screen bg-gradient-to-br from-teal-600 via-teal-500 to-emerald-500 flex flex-col">
        {/* Animated Particles Background */}
        <div className="fixed inset-0 overflow-hidden pointer-events-none z-0">
          <div className="absolute w-2 h-2 bg-white/20 rounded-full animate-float" style={{ top: '10%', left: '5%', animationDelay: '0s', animationDuration: '6s' }}></div>
          <div className="absolute w-3 h-3 bg-yellow-300/40 rounded-full animate-float" style={{ top: '20%', left: '15%', animationDelay: '1s', animationDuration: '8s' }}></div>
          <div className="absolute w-2 h-2 bg-white/20 rounded-full animate-float" style={{ top: '40%', left: '10%', animationDelay: '2s', animationDuration: '7s' }}></div>
          <div className="absolute w-4 h-4 bg-teal-300/30 rounded-full animate-float" style={{ top: '60%', left: '8%', animationDelay: '3s', animationDuration: '9s' }}></div>
          <div className="absolute w-2 h-2 bg-white/20 rounded-full animate-float" style={{ top: '80%', left: '12%', animationDelay: '4s', animationDuration: '6s' }}></div>
          <div className="absolute w-3 h-3 bg-yellow-300/40 rounded-full animate-float" style={{ top: '15%', right: '8%', animationDelay: '1.5s', animationDuration: '7s' }}></div>
          <div className="absolute w-2 h-2 bg-white/20 rounded-full animate-float" style={{ top: '35%', right: '15%', animationDelay: '2.5s', animationDuration: '8s' }}></div>
          <div className="absolute w-4 h-4 bg-teal-300/30 rounded-full animate-float" style={{ top: '55%', right: '10%', animationDelay: '3.5s', animationDuration: '6s' }}></div>
          <div className="absolute w-2 h-2 bg-white/20 rounded-full animate-float" style={{ top: '75%', right: '12%', animationDelay: '4.5s', animationDuration: '9s' }}></div>
        </div>

        {/* Decorative Navigation Strip - Vanuatu Sand Pattern */}
        <div className="w-full h-2 bg-gradient-to-r from-yellow-400 via-teal-500 to-emerald-500 relative overflow-hidden">
          <svg className="absolute inset-0 w-full h-full" xmlns="http://www.w3.org/2000/svg" preserveAspectRatio="none">
            <defs>
              <pattern id="sandPatternRegister" x="0" y="0" width="80" height="8" patternUnits="userSpaceOnUse">
                <line x1="0" y1="4" x2="15" y2="4" stroke="white" strokeWidth="0.5" opacity="0.4" />
                <line x1="20" y1="2" x2="35" y2="2" stroke="white" strokeWidth="0.5" opacity="0.3" />
                <line x1="20" y1="6" x2="35" y2="6" stroke="white" strokeWidth="0.5" opacity="0.3" />
                <line x1="40" y1="4" x2="55" y2="4" stroke="white" strokeWidth="0.5" opacity="0.4" />
                <line x1="60" y1="3" x2="70" y2="3" stroke="white" strokeWidth="0.5" opacity="0.3" />
                <line x1="60" y1="5" x2="70" y2="5" stroke="white" strokeWidth="0.5" opacity="0.3" />
                <circle cx="75" cy="4" r="0.8" fill="white" opacity="0.3" />
              </pattern>
            </defs>
            <rect width="100%" height="100%" fill="url(#sandPatternRegister)" />
          </svg>
        </div>

        {/* Main Content */}
        <div className="flex-1 flex">
          {/* Left Side - Brand Section (same layout style as Login) */}
          <div className="hidden lg:flex lg:w-1/2 relative overflow-hidden bg-gradient-to-br from-teal-600 via-teal-700 to-emerald-600">
            {/* Background Light Effects */}
            <div className="absolute inset-0 opacity-10">
              <div className="absolute top-20 left-20 w-64 h-64 bg-yellow-300 rounded-full blur-3xl"></div>
              <div className="absolute bottom-20 right-20 w-96 h-96 bg-teal-300 rounded-full blur-3xl"></div>
            </div>

            {/* Subtle pattern overlay */}
            <div className="absolute inset-0 opacity-10">
              <svg className="w-full h-full" xmlns="http://www.w3.org/2000/svg">
                <g stroke="white" strokeWidth="2" fill="none" strokeLinecap="round">
                  <path d="M 100,100 Q 120,80 140,100 Q 160,120 140,140 Q 120,160 100,140 Q 80,120 100,100" opacity="0.6" />
                  <path d="M 90,90 Q 120,60 150,90 Q 180,120 150,150 Q 120,180 90,150 Q 60,120 90,90" opacity="0.5" />
                </g>
                <g stroke="white" strokeWidth="1.5" fill="none" strokeLinecap="round" strokeDasharray="5,5">
                  <line x1="150" y1="150" x2="300" y2="200" opacity="0.3" />
                  <line x1="450" y1="250" x2="600" y2="150" opacity="0.3" />
                </g>
              </svg>
            </div>

            <div className="relative z-10 flex flex-col justify-between p-12 text-white">
              <div>
                <div className="mb-12 flex justify-center">
                  <Logo size={280} color="white" variant="full" />
                </div>

                <div className="space-y-8 max-w-md mx-auto text-center">
                  <div>
                    <span className="inline-block px-4 py-2 bg-white/20 backdrop-blur-sm rounded-full text-sm font-medium mb-6">
                      Join Your Community
                    </span>
                    <h2 className="text-4xl font-bold leading-tight mb-4">
                      Create Your Account
                    </h2>
                    <p className="text-lg text-white/90 leading-relaxed">
                      Create your account to manage savings groups, track contributions, and build stronger financial futures—together.
                    </p>
                  </div>

                  <div className="space-y-4 pt-4">
                    <div className="flex items-start gap-4 p-4 bg-white/10 backdrop-blur-sm rounded-xl">
                      <div className="w-12 h-12 bg-white/20 backdrop-blur-sm rounded-lg flex items-center justify-center flex-shrink-0">
                        <Icon name="Shield" size={22} />
                      </div>
                      <div className="flex-1 text-left">
                        <h3 className="font-semibold text-lg mb-1">Secure & Transparent</h3>
                        <p className="text-sm text-white/80">Bank-level security with complete audit trails.</p>
                      </div>
                    </div>

                    <div className="flex items-start gap-4 p-4 bg-white/10 backdrop-blur-sm rounded-xl">
                      <div className="w-12 h-12 bg-white/20 backdrop-blur-sm rounded-lg flex items-center justify-center flex-shrink-0">
                        <Icon name="Users" size={22} />
                      </div>
                      <div className="flex-1 text-left">
                        <h3 className="font-semibold text-lg mb-1">Member Management</h3>
                        <p className="text-sm text-white/80">Register members and keep group records consistent.</p>
                      </div>
                    </div>

                    <div className="flex items-start gap-4 p-4 bg-white/10 backdrop-blur-sm rounded-xl">
                      <div className="w-12 h-12 bg-white/20 backdrop-blur-sm rounded-lg flex items-center justify-center flex-shrink-0">
                        <Icon name="BarChart3" size={22} />
                      </div>
                      <div className="flex-1 text-left">
                        <h3 className="font-semibold text-lg mb-1">Reports & Insights</h3>
                        <p className="text-sm text-white/80">Track contributions and performance over time.</p>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              <div className="pt-6 border-t border-white/20 text-sm text-white/70">
                © 2026 communitySaving
              </div>
            </div>
          </div>

          {/* Right Side - Register Form */}
          <div className="w-full lg:w-1/2 flex items-center justify-center p-8 relative z-10">
            <div className="w-full max-w-md">
              {/* Mobile Logo + Sign In Link */}
              <div className="lg:hidden mb-8 flex items-center justify-between">
                <Logo size={36} color="default" variant="full" />
                <Link to="/login" className="text-sm text-white/90 hover:text-white transition-colors">
                  Sign In
                </Link>
              </div>

              <div className="bg-white rounded-2xl shadow-strong p-8 border border-gray-100">
                <div className="mb-6">
                  <h2 className="text-2xl font-bold text-gray-900 mb-2">Create your account</h2>
                  <p className="text-sm text-gray-600">Fill in your details to get started</p>
                </div>

                <MobileAppDownloads className="mb-6" />

                {errors.general && (
                  <div className={`mb-4 p-3 rounded-md ${
                    errors.general.includes('successful')
                      ? 'bg-success/10 text-success border border-success/20'
                      : 'bg-destructive/10 text-destructive border border-destructive/20'
                  }`}>
                    <p className="text-sm">{errors.general}</p>
                  </div>
                )}

                <form onSubmit={handleSubmit} className="space-y-6">
                  <RoleSelector
                    selectedRole={formData.role}
                    onRoleChange={(role) => {
                      handleInputChange('role', role);
                      // Clear email/phone errors when switching roles
                      setErrors(prev => ({ ...prev, email: undefined, phone: undefined }));

                      if (role === 'admin') {
                        // Clear member-only fields
                        setFormData((prev) => ({ ...prev, nationalId: '' }));
                        handleIdPhotoChange(null);
                        setErrors((prev) => ({ ...prev, nationalId: undefined, idPhoto: undefined }));
                      }
                    }}
                    error={touched.role ? errors.role : undefined}
                  />

                  <Input
                    label="Full Name"
                    type="text"
                    placeholder="Enter your full name"
                    value={formData.fullName}
                    onChange={(e) => handleInputChange('fullName', e.target.value)}
                    onBlur={() => handleBlur('fullName')}
                    error={touched.fullName ? errors.fullName : undefined}
                    required
                  />

                  {formData.role === 'admin' ? (
                    <Input
                      label="Email Address"
                      type="email"
                      placeholder="Enter your email"
                      value={formData.email}
                      onChange={(e) => handleInputChange('email', e.target.value)}
                      onBlur={() => handleBlur('email')}
                      error={touched.email ? errors.email : undefined}
                      description="We'll never share your email with anyone"
                      required
                    />
                  ) : (
                    <>
                      <Input
                        label="Phone Number"
                        type="tel"
                        placeholder="5555555 or +6785555555"
                        value={formData.phone}
                        onChange={(e) => handleInputChange('phone', e.target.value)}
                        onBlur={() => handleBlur('phone')}
                        error={touched.phone ? errors.phone : undefined}
                        description="Enter 7-10 digits (country code +678 added automatically)"
                        required
                      />

                      <Input
                        label="National ID"
                        type="text"
                        placeholder="Enter your National ID number"
                        value={formData.nationalId}
                        onChange={(e) => handleInputChange('nationalId', e.target.value)}
                        onBlur={() => handleBlur('nationalId')}
                        error={touched.nationalId ? errors.nationalId : undefined}
                        description="This helps your group admin verify your identity."
                        required
                      />

                      <div className="space-y-2">
                        <Input
                          label="National ID Photo"
                          type="file"
                          accept="image/jpeg,image/png,image/jpg,image/webp"
                          onChange={(e) => {
                            const file = (e.target as HTMLInputElement).files?.[0] || null;
                            handleIdPhotoChange(file);
                          }}
                          error={touched.idPhoto ? errors.idPhoto : undefined}
                          required
                        />

                        {idPhotoPreviewUrl && (
                          <div className="border border-border rounded-md overflow-hidden bg-muted/20">
                            <img
                              src={idPhotoPreviewUrl}
                              alt="National ID preview"
                              className="w-full max-h-56 object-contain"
                            />
                          </div>
                        )}
                      </div>
                    </>
                  )}

                  <div>
                    <div className="relative">
                      <Input
                        label="Password"
                        type={showPassword ? 'text' : 'password'}
                        placeholder="Create a strong password"
                        value={formData.password}
                        onChange={(e) => handleInputChange('password', e.target.value)}
                        onBlur={() => handleBlur('password')}
                        error={touched.password ? errors.password : undefined}
                        required
                      />
                      <button
                        type="button"
                        onClick={() => setShowPassword(!showPassword)}
                        className="absolute right-3 top-9 text-muted-foreground hover:text-foreground transition-interactive"
                      >
                        <Icon name={showPassword ? 'EyeOff' : 'Eye'} size={20} />
                      </button>
                    </div>
                    {formData.password && <PasswordStrengthIndicator strength={passwordStrength} />}
                  </div>

                  <div className="relative">
                    <Input
                      label="Confirm Password"
                      type={showConfirmPassword ? 'text' : 'password'}
                      placeholder="Re-enter your password"
                      value={formData.confirmPassword}
                      onChange={(e) => handleInputChange('confirmPassword', e.target.value)}
                      onBlur={() => handleBlur('confirmPassword')}
                      error={touched.confirmPassword ? errors.confirmPassword : undefined}
                      required
                    />
                    <button
                      type="button"
                      onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                      className="absolute right-3 top-9 text-muted-foreground hover:text-foreground transition-interactive"
                    >
                      <Icon name={showConfirmPassword ? 'EyeOff' : 'Eye'} size={20} />
                    </button>
                  </div>

                  {formData.role === 'admin' && (
                    <div className="space-y-3">
                      <label className="block text-sm font-medium text-foreground">
                        Select Subscription Plan <span className="text-destructive">*</span>
                      </label>
                      {isLoadingPlans && (
                        <div className="flex items-center gap-2 text-sm text-muted-foreground py-4">
                          <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-primary"></div>
                          Loading subscription plans from database...
                        </div>
                      )}
                      {!isLoadingPlans && plans.length === 0 && (
                        <p className="text-sm text-destructive py-4">
                          No subscription plans available. Please contact support.
                        </p>
                      )}
                      {!isLoadingPlans && plans.length > 0 && (
                        <div className="grid grid-cols-1 gap-3">
                          {plans.map((plan) => (
                            <button
                              key={plan.id}
                              type="button"
                              onClick={() => setSelectedPlan(plan.id)}
                              className={`
                                relative p-4 rounded-md border-2 transition-all text-left
                                ${
                                  selectedPlan === plan.id
                                    ? 'border-primary bg-primary/5'
                                    : 'border-border bg-card hover:border-primary/50'
                                }
                              `}
                            >
                              <div className="flex items-start justify-between">
                                <div className="flex-1">
                                  <div className="font-medium text-foreground mb-1">{plan.name}</div>
                                  <div className="text-sm text-muted-foreground mb-2">
                                    {plan.features && plan.features.length > 0
                                      ? plan.features.slice(0, 2).join(', ') + (plan.features.length > 2 ? '...' : '')
                                      : `${plan.duration_days} days duration`
                                    }
                                  </div>
                                  <div className="text-lg font-bold text-primary">
                                    VT {plan.price_vatu.toLocaleString()}
                                    {plan.duration_days === 365 ? '/year' : plan.duration_days === 30 ? '/month' : `/${plan.duration_days} days`}
                                  </div>
                                  {plan.plan_type && (
                                    <div className="text-xs text-muted-foreground mt-1">
                                      {plan.plan_type === 'free' ? 'Free Plan' : plan.plan_type === 'paid' ? 'Paid Plan' : plan.plan_type}
                                    </div>
                                  )}
                                </div>
                                {selectedPlan === plan.id && (
                                  <div className="ml-4 flex-shrink-0">
                                    <Icon name="CheckCircle2" size={20} color="var(--color-primary)" />
                                  </div>
                                )}
                              </div>
                            </button>
                          ))}
                        </div>
                      )}
                      {!isLoadingPlans && plans.length > 0 && !selectedPlan && (
                        <p className="text-sm text-destructive mt-2">
                          Please select a subscription plan
                        </p>
                      )}
                    </div>
                  )}

                  <Checkbox
                    label={
                      <span className="text-sm text-muted-foreground">
                        I agree to the{' '}
                        <Link to="/terms" className="text-primary hover:underline">
                          Terms of Service
                        </Link>{' '}
                        and{' '}
                        <Link to="/privacy" className="text-primary hover:underline">
                          Privacy Policy
                        </Link>
                      </span>
                    }
                    checked={formData.agreeToTerms}
                    onChange={(e) => handleInputChange('agreeToTerms', e.target.checked)}
                    error={touched.agreeToTerms ? errors.agreeToTerms : undefined}
                    required
                  />

                  <SecurityFeatures />

                  <Button
                    type="submit"
                    variant="default"
                    size="lg"
                    fullWidth
                    loading={isLoading}
                    disabled={isLoading}
                  >
                    {isLoading ? 'Creating Account...' : 'Create Account'}
                  </Button>

                  <SocialRegistration
                    providers={socialProviders}
                    onProviderClick={handleSocialRegister}
                  />

                  <div className="text-center">
                    <p className="text-sm text-muted-foreground">
                      Already have an account?{' '}
                      <Link
                        to="/login"
                        className="text-primary font-medium hover:underline transition-interactive"
                      >
                        Sign in instead
                      </Link>
                    </p>
                  </div>
                </form>
              </div>

              <div className="mt-6 text-center space-y-3">
                <p className="text-xs text-white/80">
                  By creating an account, you agree to our data protection policies and consent to the processing of your information for community contribution management purposes.
                </p>
              </div>
            </div>
          </div>
        </div>
    </div>
    </>
  );
};

export default Register;